import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { passwordResetService } from '../services/passwordResetService';
import type { ForgotPasswordRequest } from '../services/passwordResetService';
import { Card, CardContent } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const [resetRequested, setResetRequested] = useState(false);
  const { showNotification } = useNotification();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequest>();

  const forgotPasswordMutation = useMutation({
    mutationFn: passwordResetService.forgotPassword,
    onSuccess: (data) => {
      const message = data.message || t('auth.forgotPassword.success');
      showNotification({
        type: 'success',
        title: t('auth.forgotPassword.emailSent', 'Reset Email Sent'),
        message: message
      });
      setResetRequested(true);
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('auth.forgotPassword.errorTitle', 'Error Sending Reset Email'),
        message: error?.response?.data?.error || t('auth.forgotPassword.errorMessage', 'Failed to send reset instructions. Please try again.')
      });
    }
  });

  const onSubmit = (data: ForgotPasswordRequest) => {
    forgotPasswordMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.forgotPassword.title', 'Zapomniałeś hasła?')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.forgotPassword.description', 'Wprowadź swój adres email, a wyślemy Ci instrukcje resetowania hasła')}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            {resetRequested ? (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        {t('auth.forgotPassword.success', 'Link do resetowania hasła został wysłany na Twój adres email')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.forgotPassword.backToSignIn', 'Powrót do logowania')}
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  label={t('auth.email', 'Adres email')}
                  type="email"
                  autoComplete="email"
                  required
                  {...register('email', {
                    required: t('auth.forgotPassword.emailRequired', 'Email jest wymagany'),
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: t('auth.forgotPassword.invalidEmail', 'Nieprawidłowy adres email'),
                    },
                  })}
                  error={errors.email?.message}
                />

                <div>
                  <Button
                    type="submit"
                    className="w-full"
                    isLoading={forgotPasswordMutation.isPending}
                    disabled={forgotPasswordMutation.isPending}
                  >
{t('auth.forgotPassword.sendInstructions', 'Wyślij instrukcje resetowania')}
                  </Button>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.forgotPassword.backToSignIn', 'Powrót do logowania')}
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};